import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useLocation } from '../../contexts/LocationContext';
import apiClient from '../../utils/api';

interface DeathRecord {
  tag: string;
  category: string;
  death_date: string;
  age_at_death_months?: number;
  cause?: string;
  shed_id?: string;
  shed_name?: string;
  purchase_amount?: number;
}

interface MortalityByCategory {
  category: string;
  deaths: number;
  percentage: number;
  average_age_at_death_months?: number;
}

interface MortalityByCause {
  cause: string;
  deaths: number;
  percentage: number;
}

interface MortalityByAgeGroup {
  age_group: string;
  deaths: number;
  percentage: number;
}

interface GoatMortalityReport {
  location_id: string;
  location_name: string;
  report_date: string;
  period_start: string;
  period_end: string;
  total_deaths: number;
  mortality_rate: number;
  total_financial_loss: number;
  death_records: DeathRecord[];
  mortality_by_category: MortalityByCategory[];
  mortality_by_cause: MortalityByCause[];
  mortality_by_age_group: MortalityByAgeGroup[];
  monthly_trends: Array<{ month: string; deaths: number }>;
}

const GoatMortalityReportPage: React.FC = () => {
  const { selectedLocation } = useLocation();
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [report, setReport] = useState<GoatMortalityReport | null>(null);

  useEffect(() => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const januaryFirst = new Date(`${currentYear}-01-01T00:00:00+05:30`);
    
    const formatDate = (date: Date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    setEndDate(formatDate(today));
    setStartDate(formatDate(januaryFirst));
  }, []);

  useEffect(() => {
    if (selectedLocation?.id && startDate && endDate) {
      fetchReport();
    }
  }, [selectedLocation?.id, startDate, endDate]);

  const fetchReport = async () => {
    if (!selectedLocation || !startDate || !endDate) return;
    
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get('/goat/report/mortality', {
        params: {
          location_id: selectedLocation.id,
          start: startDate,
          end: endDate,
        },
      });
      setReport(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to load mortality report');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
    }).format(amount);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN');
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return <div className="container-mobile py-6 text-center">Loading...</div>;
  }

  return (
    <div className="container-mobile py-6 space-y-6">
      <div className="text-center">
        <ExclamationTriangleIcon className="h-16 w-16 text-red-600 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Mortality & Loss Analysis Report</h1>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex flex-col sm:flex-row gap-4 items-end">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-600" />
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {report && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl shadow-sm border border-red-100 p-4 border-red-300">
              <div className="text-2xl font-bold text-red-600">{report.total_deaths}</div>
              <div className="text-sm text-gray-600">Total Deaths</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-red-100 p-4 border-red-300">
              <div className="text-2xl font-bold text-red-600">{report.mortality_rate.toFixed(2)}%</div>
              <div className="text-sm text-gray-600">Mortality Rate</div>
            </div>
            <div className="bg-white rounded-xl shadow-sm border border-red-100 p-4 border-red-300">
              <div className="text-2xl font-bold text-red-600">{formatCurrency(report.total_financial_loss)}</div>
              <div className="text-sm text-gray-600">Financial Loss</div>
            </div>
          </div>

          {report.mortality_by_category.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Mortality by Category</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deaths</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percentage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Avg Age at Death</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.mortality_by_category.map((cat, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 capitalize">{cat.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cat.deaths}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cat.percentage.toFixed(1)}%</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {cat.average_age_at_death_months ? `${cat.average_age_at_death_months.toFixed(1)} months` : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.mortality_by_cause.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Mortality by Cause</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cause</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deaths</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Percentage</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.mortality_by_cause.map((cause, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{cause.cause}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cause.deaths}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{cause.percentage.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {report.death_records.length > 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Death Records</h2>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tag</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Death Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Age</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cause</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loss</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {report.death_records.map((record, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{record.tag}</td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{record.category}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{formatDate(record.death_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {record.age_at_death_months ? `${record.age_at_death_months} months` : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{record.cause || 'Unknown'}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {record.purchase_amount ? formatCurrency(record.purchase_amount) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GoatMortalityReportPage;

